/**
 * Allocation Response Mapping Utilities
 * 
 * This module provides utilities for mapping TEFAS allocation API responses
 * to clean, typed response objects with English property names.
 */

import { FundType, FundContentResponse, TefasAllocationApiResponse } from '../types';

/**
 * Converts a timestamp string (milliseconds) to YYYY-MM-DD format
 * 
 * @private
 * @param timestamp - Timestamp string in milliseconds
 * @returns Date string in YYYY-MM-DD format
 */
function convertTimestampToDate(timestamp: string): string {
  const date = new Date(parseInt(timestamp, 10));
  // Handle invalid timestamps
  if (isNaN(date.getTime())) {
    return '1970-01-01';
  }
  return date.toISOString().split('T')[0];
}

/**
 * Parses a comma-formatted number string to a number
 * 
 * @private
 * @param value - Comma-formatted number string (e.g., "1,234,567.89")
 * @returns Parsed number
 */
function parseCommaFormattedNumber(value: string): number {
  if (!value || value.trim() === '') {
    return 0;
  }
  
  // Remove commas and parse as float
  const cleanValue = value.replace(/,/g, '');
  return parseFloat(cleanValue) || 0;
}

/**
 * Maps TEFAS allocation API response to clean response objects
 * 
 * This function converts the raw TEFAS API response format to our clean,
 * typed response format with English property names and proper data types.
 * 
 * @param response - Raw TEFAS allocation API response
 * @param fundType - Fund type from the request
 * @returns Array of mapped fund content responses
 */
export function mapAllocationResponse(
  response: TefasAllocationApiResponse,
  fundType: FundType
): FundContentResponse[] {
  if (!response.data || !Array.isArray(response.data)) {
    return [];
  }

  return response.data.map((item) => ({
    fundCode: item.FONKODU,
    fundName: item.FONUNVAN,
    fundType,
    date: convertTimestampToDate(item.TARIH),
    totalValue: parseCommaFormattedNumber(item.BilFiyat),
    
    // Stock allocations
    stocks: item.HS ?? undefined,
    foreignSecurities: item.YYF ?? undefined,
    stockBasedFunds: item.HB ?? undefined,
    stockBasedInvestmentFunds: item.YHS ?? undefined,
    
    // Bond allocations
    bonds: item.BB ?? undefined,
    governmentBonds: item.DB ?? undefined,
    privateSectorBonds: item.ÖSDB ?? undefined,
    governmentSecurities: item.GSYKB ?? undefined,
    governmentSecuritiesYield: item.GSYY ?? undefined,
    governmentYieldBonds: item.GYKB ?? undefined,
    governmentYield: item.GYY ?? undefined,
    
    // Deposit and cash allocations
    deposits: item.D ?? undefined,
    reversePurchaseAgreements: item.T ?? undefined,
    preciousMetals: item.KM ?? undefined,
    cashAndCashEquivalents: item.OSKS ?? undefined,
    otherSecurities: item.OSKSYD ?? undefined,
    otherSecuritiesYield: item.ÖKSYD ?? undefined,
    
    // Treasury and government instruments
    treasuryBills: item.BPP ?? undefined,
    individualInvestmentFunds: item.BYF ?? undefined,
    governmentDebtInstruments: item.DT ?? undefined,
    governmentDebtInstrumentsForeign: item.DÖT ?? undefined,
    eurobonds: item.EUT ?? undefined,
    foreignBonds: item.FB ?? undefined,
    foreignCurrencyBonds: item.FKB ?? undefined,
    goldAndSilver: item.GAS ?? undefined,
    
    // Participation accounts and certificates
    participationAccounts: item.KBA ?? undefined,
    participationAccountsGold: item.KH ?? undefined,
    participationAccountsGoldForeign: item.KHAU ?? undefined,
    participationAccountsForeignCurrency: item.KHD ?? undefined,
    participationAccountsTurkishLira: item.KHTL ?? undefined,
    publicLeaseCertificates: item.KKS ?? undefined,
    publicLeaseCertificatesForeignCurrency: item.KKSD ?? undefined,
    publicLeaseCertificatesTurkishLira: item.KKSTL ?? undefined,
    publicLeaseCertificatesForeign: item.KKSYD ?? undefined,
    participationAccountsForeignCurrencyBonds: item.KİBD ?? undefined,
    
    // Other instruments
    otherSecuritiesTurkishLira: item.OST ?? undefined,
    repoTransactions: item.R ?? undefined,
    termDeposits: item.TPP ?? undefined,
    treasuryBillsShort: item.TR ?? undefined,
    variableRateDeposits: item.VDM ?? undefined,
    moneyMarketInstruments: item.VM ?? undefined,
    moneyMarketInstrumentsGold: item.VMAU ?? undefined,
    moneyMarketInstrumentsForeignCurrency: item.VMD ?? undefined,
    moneyMarketInstrumentsTurkishLira: item.VMTL ?? undefined,
    variableIncomeInstruments: item.VİNT ?? undefined,
    
    // Investment fund participations
    investmentFundParticipations: item.YBA ?? undefined,
    investmentFundParticipationsBonds: item.YBKB ?? undefined,
    investmentFundParticipationsOtherSecurities: item.YBOSB ?? undefined,
    investmentFundParticipationsForeignCurrency: item.YBYF ?? undefined,
    investmentFundParticipationsMetals: item.YMK ?? undefined,
    
    // Precious metals and derivatives
    preciousMetalsInvestmentFunds: item.KMBYF ?? undefined,
    preciousMetalsGovernmentDebtInstruments: item.KMKBA ?? undefined,
    preciousMetalsPublicLeaseCertificates: item.KMKKS ?? undefined,
  }));
}
